import { useEffect } from "react";
import {
  GridFilterPanel,
  useGridApiContext,
  GridFilterModel,
} from "@mui/x-data-grid-premium";
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { FilterBuilderContent } from "./FilterBuilderPanel";
import { AdvancedFilterModel } from "../types";
import { GridColDef } from "@mui/x-data-grid-premium";

// ─── Module augmentation ──────────────────────────────────────────────────────
// Extends MUI X's empty FilterPanelPropsOverrides so slotProps.filterPanel
// accepts our custom props with full TypeScript support.

declare module "@mui/x-data-grid-premium" {
  interface FilterPanelPropsOverrides {
    advancedFilterModel?: AdvancedFilterModel | null;
    onAdvancedApply?: (model: AdvancedFilterModel) => void;
    onAdvancedClear?: () => void;
    filterColumns?: GridColDef[];
    // Refs owned by ServerDataGrid so stashed state survives panel remounts
    savedBasicFiltersRef?: { current: GridFilterModel | null };
    savedAdvancedFiltersRef?: { current: AdvancedFilterModel | null };
    // When set, the filter panel will pre-populate this field on open
    pendingFilterFieldRef?: { current: string | null };
    // Controlled mode — lifted to ServerDataGrid so it survives panel remounts
    mode?: "basic" | "advanced";
    onModeChange?: (mode: "basic" | "advanced") => void;
  }
}

// ─── CombinedFilterPanel ──────────────────────────────────────────────────────

export function CombinedFilterPanel(props: any) {
  const {
    advancedFilterModel,
    onAdvancedApply,
    onAdvancedClear,
    filterColumns = [],
    savedBasicFiltersRef,
    savedAdvancedFiltersRef,
    pendingFilterFieldRef,
    mode,
    onModeChange,
    // Remaining props are forwarded to GridFilterPanel in basic mode
    ...gridFilterPanelProps
  } = props;

  const apiRef = useGridApiContext();

  // On remount: if we're in basic mode and basic filters were stashed during a
  // previous session (user switched to advanced then closed without switching back),
  // restore them to MUI X state now.
  useEffect(() => {
    if (mode === "basic" && savedBasicFiltersRef?.current) {
      apiRef.current.setFilterModel(savedBasicFiltersRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep savedBasicFiltersRef in sync with every filter model change while in
  // basic mode so that additions, edits, and removals are all reflected.
  useEffect(() => {
    if (mode !== "basic" || !savedBasicFiltersRef) return;
    const unsubscribe = apiRef.current.subscribeEvent(
      "filterModelChange",
      (model: GridFilterModel) => {
        savedBasicFiltersRef.current = model;
      }
    );
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleModeChange = (newMode: "basic" | "advanced") => {
    if (!newMode) return;
    if (newMode === "advanced") {
      // Stash current basic filters. The MUI X filter model is intentionally
      // left active so the user's filtered view is preserved while they
      // configure their advanced filter. It will be cleared on Apply.
      if (savedBasicFiltersRef) {
        savedBasicFiltersRef.current =
          apiRef.current.exportState().filter?.filterModel ?? null;
      }
    }
    if (newMode === "basic") {
      // Only update the stash if there's an actively applied filter — if
      // advancedFilterModel is already null (cleared by a previous mode switch),
      // preserve whatever stash exists so it survives multiple toggle cycles.
      if (savedAdvancedFiltersRef && advancedFilterModel !== null) {
        savedAdvancedFiltersRef.current = advancedFilterModel;
      }
      onAdvancedClear?.();
      // Restore previously saved basic filters
      if (savedBasicFiltersRef?.current) {
        apiRef.current.setFilterModel(savedBasicFiltersRef.current);
      }
    }
    onModeChange?.(newMode);
  };

  // When no active advanced filter exists (e.g. after toggling to basic),
  // fall back to the stashed one so FilterBuilderContent re-populates correctly.
  const effectiveAdvancedModel =
    advancedFilterModel ?? savedAdvancedFiltersRef?.current ?? null;

  return (
    <Box>
      {/* Basic / Advanced toggle */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          p: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_, v) => handleModeChange(v)}
        >
          <ToggleButton value="basic" sx={{ px: 2 }}>
            Basic
          </ToggleButton>
          <ToggleButton value="advanced" sx={{ px: 2 }}>
            Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === "basic" ? (
        <GridFilterPanel {...gridFilterPanelProps} />
      ) : (
        <FilterBuilderContent
          columns={filterColumns}
          advancedFilterModel={effectiveAdvancedModel}
          pendingFilterFieldRef={pendingFilterFieldRef}
          onApply={(model) => {
            onAdvancedApply?.(model);
            apiRef.current.hideFilterPanel();
          }}
          onClear={() => {
            // User explicitly cleared — drop the stash so it isn't restored
            if (savedAdvancedFiltersRef) savedAdvancedFiltersRef.current = null;
            onAdvancedClear?.();
            onModeChange?.("basic");
          }}
        />
      )}
    </Box>
  );
}
